import React from 'react';
import { adaptEventHandler, useAdaptEventHandler } from '../../utils';
import type { Icon as IconType } from '@atlassian/forge-ui-types';
import type { ButtonProps as PlatformButtonProps } from '@atlaskit/button/new';
import { IconComponent } from '../icon';
import type { RenderFn } from '@atlassian/forge-ui-types';
import PlatformButton from '@atlaskit/button/new';
import { fg } from '@atlaskit/platform-feature-flags';

const createIconComponent = (icon: IconType) => {
	const Component = () => <IconComponent glyph={icon} label={icon} />;
	return Component;
};

export type ButtonProps = Pick<
	PlatformButtonProps,
	| 'children'
	| 'autoFocus'
	| 'isDisabled'
	| 'isSelected'
	| 'onBlur'
	| 'onClick'
	| 'onFocus'
	| 'testId'
	| 'shouldFitContainer'
	| 'appearance'
	| 'type'
> & {
	iconBefore?: IconType;
	iconAfter?: IconType;
	spacing?: PlatformButtonProps['spacing'] | 'none';
};

/**
 * Mapping the deprecated 'none' spacing to 'compact'
 */
const mapDeprecatedSpacing = (spacing: ButtonProps['spacing']) =>
	spacing === 'none' ? 'compact' : spacing;

const OldButton = (props: Parameters<RenderFn>[0]) => {
	const {
		autoFocus,
		isDisabled,
		isSelected,
		onBlur,
		onClick,
		onFocus,
		spacing,
		testId,
		shouldFitContainer,
		appearance,
		type,
		iconAfter,
		iconBefore,
	} = props.forgeDoc.props as ButtonProps;

	return (
		<PlatformButton
			children={props.forgeDoc.children.map((c) => (
				<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
			))}
			autoFocus={autoFocus}
			isDisabled={isDisabled}
			isSelected={isSelected}
			onBlur={adaptEventHandler(onBlur)}
			onClick={adaptEventHandler(onClick)}
			onFocus={adaptEventHandler(onFocus)}
			spacing={mapDeprecatedSpacing(spacing)}
			testId={testId}
			shouldFitContainer={shouldFitContainer}
			appearance={appearance}
			type={type}
			iconAfter={iconAfter && createIconComponent(iconAfter)}
			iconBefore={iconBefore && createIconComponent(iconBefore)}
		/>
	);
};

const ButtonWithAnalytics = (props: Parameters<RenderFn>[0]) => {
	const {
		autoFocus,
		isDisabled,
		isSelected,
		onBlur,
		onClick,
		onFocus,
		spacing,
		testId,
		shouldFitContainer,
		appearance,
		type,
		iconAfter,
		iconBefore,
	} = props.forgeDoc.props as ButtonProps;

	const adaptEventHandler = useAdaptEventHandler();

	return (
		<PlatformButton
			children={props.forgeDoc.children.map((c) => (
				<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
			))}
			autoFocus={autoFocus}
			isDisabled={isDisabled}
			isSelected={isSelected}
			onBlur={adaptEventHandler(onBlur)}
			onClick={adaptEventHandler(onClick)}
			onFocus={adaptEventHandler(onFocus)}
			spacing={mapDeprecatedSpacing(spacing)}
			testId={testId}
			shouldFitContainer={shouldFitContainer}
			appearance={appearance}
			type={type}
			iconAfter={iconAfter && createIconComponent(iconAfter)}
			iconBefore={iconBefore && createIconComponent(iconBefore)}
		/>
	);
};

/**
 * A button triggers an event or action. They let users know what will happen next.
 */
export const Button = (props: Parameters<RenderFn>[0]) => {
	if (fg('forge-ui-ui-kit-click-events')) {
		return <ButtonWithAnalytics {...props} />;
	}
	return <OldButton {...props} />;
};
